import { CommonModule } from "@angular/common";
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnDestroy,
  signal,
  viewChild,
} from "@angular/core";
import Chart from "chart.js/auto";
import { groupBy, sum, toDayKey, wAvg } from "../../services/stats.util";
import { ensureChartJs } from "../../services/chart.utils";
import { JustupColors } from "../../../../shared/enums/justup-colors.enum";
import { EntityMetadata } from "../../../../shared/store/store-types.model";
import { Widget } from "../../../dashboard/models/dashboard.model";
import { WidgetTitleComponent } from "../../../../shared/components/widget-title/widget-title.component";
import { ArticleFacadeService } from "../../services/article/article-facade.service";

// UI pour le bouton filtre dans le header
import { SharedModule } from "../../../../shared/shared.module";
import { ChipsComponent } from "../../../../shared/modules/chips/chips.component";
import { PopoverModule } from "../../../../shared/modules/popover/popover.module";
import { parseNumber } from "../../../../shared/utils/number-utils.service";

type OrderPoint = {
  key: string; // id/clé de commande
  numero: string; // numéro lisible
  dateKey: string; // YYYY-MM-DD (réception la plus récente de la commande)
  label: string; // "JJ/MM • NUMÉRO"
  pu: number; // PU moyen pondéré sur la commande
  qty: number; // quantité totale reçue pour la commande
};

@Component({
  selector: "vl-stats-achat-evolution",
  standalone: true,
  imports: [
    CommonModule,
    WidgetTitleComponent,
    SharedModule,
    ChipsComponent,
    PopoverModule,
  ],
  templateUrl: "./stats-article-achat-evolution.component.html",
  styleUrls: ["./stats-article-achat-evolution.component.scss"],
  host: { class: "widget-container" },
})
export class AchatPrixEvolutionComponent implements OnDestroy {
  metadata = input<EntityMetadata>();
  widget = input<Widget>();

  articleFacade = inject(ArticleFacadeService);
  stats = this.articleFacade.getState().stats;

  // Canvas en signal pour éviter NG0951 si le DOM n'est pas encore prêt
  canvasRef = viewChild<ElementRef<HTMLCanvasElement>>("articleAchat");
  private chart?: Chart;

  // ===== Filtres =====
  topCount = signal<number>(20); // N dernières commandes
  search = signal<string>(""); // filtre par numéro

  // ===== Données brutes => points par commande =====
  private pointsAll = computed<OrderPoint[]>(() => {
    const s = this.stats();
    const rows = s?.receptions ?? [];
    if (!rows.length) return [];

    const byOrder = groupBy(rows, (r: any) => r?.commandeId);

    const fmtFR = new Intl.DateTimeFormat("fr-FR", {
      day: "2-digit",
      month: "2-digit",
    });

    const list: OrderPoint[] = Object.keys(byOrder).map((key) => {
      const arr = byOrder[key] ?? [];
      const prix = arr.map((r: any) => parseNumber(r?.prixUnitaire ?? 0));
      const qtes = arr.map((r: any) => parseNumber(r?.quantite ?? 0));

      // date = plus récente des réceptions de la commande
      const dates = arr.map((r: any) => toDayKey(r?.date));
      const dateKey = dates.sort().at(-1) ?? toDayKey(arr[0]?.date);

      // numéro lisible (fallback clé)
      const numero = (arr[0]?.commandeLigne?.commande?.numero ??
        String(key)) as string;

      const pu = wAvg(prix, qtes) ?? 0;
      const qty = sum(qtes) ?? 0;

      // label "JJ/MM • NUMÉRO"
      const labelDate = (() => {
        try {
          return fmtFR.format(new Date(dateKey));
        } catch {
          return dateKey;
        }
      })();
      const label = `${labelDate} • ${numero}`;

      return { key, numero, dateKey, label, pu, qty };
    });

    // Tri chronologique (ASC), puis numéro pour stabilité
    return list.sort((a, b) =>
      a.dateKey === b.dateKey
        ? a.numero.localeCompare(b.numero)
        : a.dateKey.localeCompare(b.dateKey),
    );
  });

  // Recherche, puis Top N des + récentes
  private pointsFiltered = computed<OrderPoint[]>(() => {
    const q = this.search().trim().toLowerCase();
    if (!q) return this.pointsAll();
    return this.pointsAll().filter((p) => p.numero.toLowerCase().includes(q));
  });
  maxRows = computed(() => this.pointsFiltered().length);

  pointsTop = computed<OrderPoint[]>(() => {
    const n = Math.max(1, Math.min(this.topCount(), this.maxRows()));
    const arr = this.pointsFiltered();
    return arr.slice(Math.max(0, arr.length - n)); // N dernières, en ordre chrono
  });

  /** Labels pour l'axe X */
  private labels = computed<string[]>(() =>
    this.pointsTop().map((p) => p.label),
  );

  /** Dataset barres (PU par commande) */
  private barDataset = computed(() => {
    const data = this.pointsTop().map((p) => p.pu);
    return {
      type: "bar" as const,
      label: "PU par commande",
      data,
      backgroundColor: JustupColors.BLEU_CLAIR,
      borderColor: JustupColors.BLEU,
      borderWidth: 1,
      maxBarThickness: 36,
      order: 2,
    };
  });

  /** Ligne moyenne pondérée cumulée (pondérée par qty) sur l’échantillon filtré */
  private avgDataset = computed(() => {
    const pts = this.pointsTop();
    let accPV = 0; // somme(pu * qty)
    let accQ = 0; // somme(qty)
    const data = pts.map((p) => {
      accPV += (p.pu ?? 0) * (p.qty ?? 0);
      accQ += p.qty ?? 0;
      return accQ > 0 ? accPV / accQ : 0;
    });
    return {
      type: "line" as const,
      label: "Moyenne pondérée cumulée",
      data,
      borderColor: JustupColors.VIOLET,
      pointRadius: 2,
      pointHoverRadius: 4,
      tension: 0.2,
      fill: false,
      order: 1,
    };
  });

  constructor() {
    // Charger ChartJS si nécessaire et construire le chart quand tout est prêt

    effect(
      () => {
        // crée une dépendance sur stats()
        this.stats();
        const max = this.maxRows();
        this.topCount.set(max);
      },
      { allowSignalWrites: true },
    );

    effect(
      () => {
        ensureChartJs();
        if (this.canvasRef() && this.pointsTop().length) {
          this.buildChart();
        } else {
          this.destroyChart();
        }
      },
      { allowSignalWrites: true },
    );

    // Mettre à jour le chart en live (labels + datasets)
    effect(
      () => {
        this.updateChart();
      },
      { allowSignalWrites: true },
    );

    // Clamp du slider si le max change
    effect(
      () => {
        const max = this.maxRows();
        const cur = this.topCount();
        const clamped = Math.min(Math.max(1, cur), Math.max(1, max));
        if (clamped !== cur) this.topCount.set(clamped);
      },
      { allowSignalWrites: true },
    );
  }

  ngOnDestroy(): void {
    this.destroyChart();
  }

  // ===== Chart =====
  private buildChart(): void {
    const canvas = this.canvasRef();
    const ctx = canvas?.nativeElement.getContext("2d");
    if (!ctx) return;

    // Récup palette depuis le thème
    const css = getComputedStyle(canvas!.nativeElement);
    const BG_ALT = (
      css.getPropertyValue("--color-on-background-alt") || "#476373"
    ).trim();
    const OUTLINE = (
      css.getPropertyValue("--color-outline") || "#9db8c3"
    ).trim();
    const ACCENT = (css.getPropertyValue("--color-accent") || "#c535ff").trim();
    const ON_ACCENT = (
      css.getPropertyValue("--color-on-accent") || "#ffffff"
    ).trim();
    const ON_ACCENT_ALT = (
      css.getPropertyValue("--color-on-accent-alt") || "rgba(197,53,255,.35)"
    ).trim();

    this.destroyChart?.();

    // Clone des datasets pour override du style (bar + line)
    const bar = { ...(this.barDataset() as any) };
    bar.backgroundColor = ON_ACCENT_ALT;
    bar.borderColor = "transparent";
    bar.borderWidth = 0;
    bar.maxBarThickness = bar.maxBarThickness ?? 36;

    const line = { ...(this.avgDataset() as any) };
    line.borderColor = ACCENT;
    line.backgroundColor = "rgba(0,0,0,0)";
    line.borderWidth = 5;
    line.pointBackgroundColor = ACCENT;
    line.pointBorderColor = ON_ACCENT;
    line.pointBorderWidth = 3;
    line.pointRadius = 7;
    line.fill = false;
    line.tension = 0.2;

    this.chart = new Chart(ctx, {
      type: "bar", // mixte via type par dataset
      data: {
        labels: this.labels(),
        datasets: [bar, line],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
          legend: { display: false }, // aligné avec les autres widgets
          tooltip: {
            callbacks: {
              title: (items) => items?.[0]?.label ?? "",
              label: (ctx) => {
                const idx = ctx.dataIndex ?? 0;
                const p =
                  (this as any).pointsTop?.()[idx] ??
                  (this as any).points?.()[idx];
                const eur = (v: number) =>
                  new Intl.NumberFormat("fr-FR", {
                    style: "currency",
                    currency: "EUR",
                    maximumFractionDigits: 4,
                  }).format(v ?? 0);
                if (ctx.dataset.type === "bar") {
                  return `PU commande: ${eur(ctx.parsed.y)}  •  Qté: ${p?.qty ?? 0}`;
                }
                return `Moyenne pondérée cumulée: ${eur(ctx.parsed.y)}`;
              },
            },
          },
        },
        scales: {
          y: {
            grid: { color: OUTLINE },
            border: { color: OUTLINE },
            ticks: {
              color: BG_ALT,
              callback: (v) =>
                new Intl.NumberFormat("fr-FR", {
                  style: "currency",
                  currency: "EUR",
                  maximumFractionDigits: 2,
                }).format(parseNumber(v)),
            },
          },
          x: {
            grid: { display: false },
            border: { color: OUTLINE },
            ticks: {
              color: BG_ALT,
              maxRotation: 0,
              autoSkip: true,
              autoSkipPadding: 8,
            },
          },
        },
        elements: {
          line: { borderWidth: 2 },
          point: { hitRadius: 8, hoverRadius: 4 },
        },
        layout: { padding: { left: 6, right: 12, top: 6, bottom: 6 } },
      },
    });
  }

  private updateChart(): void {
    if (!this.chart) return;

    this.chart.data.labels = this.labels() as any;

    const bar = this.barDataset();
    const line = this.avgDataset();

    if ((this.chart.data.datasets?.length ?? 0) < 2) {
      this.chart.data.datasets = [bar as any, line as any];
    } else {
      // dataset 0 = barres
      const d0 = this.chart.data.datasets[0] as any;
      d0.type = bar.type;
      d0.label = bar.label;
      d0.data = bar.data;
      d0.backgroundColor = bar.backgroundColor;
      d0.borderColor = bar.borderColor;
      d0.borderWidth = bar.borderWidth;
      d0.maxBarThickness = bar.maxBarThickness;

      // dataset 1 = ligne moyenne cumulée
      const d1 = this.chart.data.datasets[1] as any;
      d1.type = line.type;
      d1.label = line.label;
      d1.data = line.data;
      d1.borderColor = line.borderColor;
      d1.pointRadius = line.pointRadius;
      d1.pointHoverRadius = line.pointHoverRadius;
      d1.tension = line.tension;
      d1.fill = line.fill;
    }

    this.chart.update("none");
  }

  private destroyChart(): void {
    if (this.chart) {
      this.chart.destroy();
      this.chart = undefined;
    }
  }

  protected readonly Math = Math;
}
